import { config } from '@iheartjane/config';
import { useInfiniteQuery } from '@tanstack/react-query';

import { getJwt } from '@jane/shared/auth';
import { encodeQuery } from '@jane/shared/util';

import { api } from '../api/api';
import { createUseQueryHook } from '../utils/createUseQueryHook';
import { withAuthHeader } from '../utils/withAuthHeader';
import type {
  GetTargetedBrandSpecialsReturnType,
  TargetedBrandSpecialsProps,
} from './targetedBrandSpecials.types';
import { targetedBrandSpecialsKeys } from './targetedBrandSpecials.util';

export const targetedBrandSpecialUrl = () =>
  `${config.apiPathV2}/brand_specials`;

export const getTargetedBrandSpecials = async ({
  page,
  perPage,
  storeIds,
  appMode,
}: TargetedBrandSpecialsProps): Promise<GetTargetedBrandSpecialsReturnType | null> => {
  const params = {
    page,
    per_page: perPage,
    store_id: storeIds,
    app_mode: appMode,
  };

  if (!storeIds) return null;
  const jwt = getJwt();

  const url = encodeQuery(targetedBrandSpecialUrl(), params, {
    arrayFormat: 'brackets',
  });

  return await api.get<GetTargetedBrandSpecialsReturnType>(
    url,
    withAuthHeader({}, jwt)
  );
};

export const useTargetedBrandSpecials = createUseQueryHook<
  TargetedBrandSpecialsProps,
  GetTargetedBrandSpecialsReturnType | null
>(
  getTargetedBrandSpecials,
  targetedBrandSpecialsKeys.brandSpecials,
  (params) => !!params.storeIds?.length
);

export const useInfiniteTargetedBrandSpecials = ({
  ...params
}: {
  perPage: number;
  storeIds: (string | number)[];
}) => {
  return useInfiniteQuery({
    enabled: !!params.storeIds?.length,
    queryFn: async ({ pageParam = 0 }) => {
      const targetedBrandSpecials = await getTargetedBrandSpecials({
        ...params,
        page: pageParam + 1,
      });

      return { ...targetedBrandSpecials, pageParam };
    },
    queryKey: targetedBrandSpecialsKeys.infiniteBrandSpecials(params),
    getNextPageParam: (lastPage) => {
      const isLastPage =
        (lastPage.brand_specials || []).length < params.perPage;
      return isLastPage ? undefined : lastPage.pageParam + 1;
    },
  });
};
